import { z } from 'zod';
import { EntitySchema } from './shared.schema';

/**
 * Reference to a world entity pinned on the map.
 */
export const MapEntityRefSchema = z.object({
  type: z.enum(['locations', 'events']),
  id: z.string(),
  name: z.string(),
});
export type MapEntityRef = z.infer<typeof MapEntityRefSchema>;

/**
 * A single entity point placed on the map (pin/marker).
 */
export const MapEntityPointSchema = z.object({
  uid: z.string(),
  entityRef: MapEntityRefSchema,
  x: z.number(),
  y: z.number(),
  label: z.string().optional().describe('Display label override; defaults to entityRef.name'),
});
export type MapEntityPoint = z.infer<typeof MapEntityPointSchema>;

/**
 * Visual style for a map area polygon.
 */
export const MapAreaStyleSchema = z.object({
  fillColor: z.string().default('#4a90d9'),
  fillOpacity: z.number().min(0).max(1).default(0.2),
  borderColor: z.string().default('#2563eb'),
  borderWidth: z.number().min(0).default(2),
  dashPattern: z.array(z.number()).default([]),
});
export type MapAreaStyle = z.infer<typeof MapAreaStyleSchema>;

/**
 * A polygon area drawn on the map, linked to a Location.
 */
export const MapEntityAreaSchema = z.object({
  uid: z.string(),
  entityRef: MapEntityRefSchema.extend({ type: z.literal('locations') }),
  vertices: z.array(z.object({ x: z.number(), y: z.number() })),
  style: MapAreaStyleSchema,
});
export type MapEntityArea = z.infer<typeof MapEntityAreaSchema>;

// ---------------------------------------------------------------------------
// Path feature
// ---------------------------------------------------------------------------

/** Supported path types — controls default styling and organization. */
export const MAP_PATH_TYPES = ['road', 'river', 'route', 'custom'] as const;
export type MapPathType = typeof MAP_PATH_TYPES[number];

/**
 * Road quality sub-classification, only meaningful when pathType === 'road'.
 * Influences default stroke appearance (width, dash).
 * Future: may drive legend/filtering.
 */
export const MAP_ROAD_QUALITIES = ['trail', 'dirt', 'cobbled', 'paved'] as const;
export type MapRoadQuality = typeof MAP_ROAD_QUALITIES[number];

/** Visual style for a drawn path (polyline). */
export const MapPathStyleSchema = z.object({
  strokeColor:  z.string().default('#8B7355'),
  strokeWidth:  z.number().min(0.5).max(20).default(3),
  opacity:      z.number().min(0).max(1).default(1),
  dashed:       z.boolean().default(false),
  lineCap:      z.enum(['butt', 'round', 'square']).default('round'),
  lineJoin:     z.enum(['miter', 'round', 'bevel']).default('round'),
});
export type MapPathStyle = z.infer<typeof MapPathStyleSchema>;

/**
 * A single vertex of a path.
 * Kept minimal for v1; control-point fields for Bézier may be added later
 * without breaking existing data.
 */
export const MapPathVertexSchema = z.object({
  x: z.number(),
  y: z.number(),
  // Future: cp1x, cp1y, cp2x, cp2y for cubic Bézier control points
});
export type MapPathVertex = z.infer<typeof MapPathVertexSchema>;

/**
 * A generic polyline element placed on the map.
 * pathType drives default styling; style can be fully customised afterward.
 */
export const MapEntityPathSchema = z.object({
  uid:         z.string(),
  pathType:    z.enum(MAP_PATH_TYPES).default('road'),
  roadQuality: z.enum(MAP_ROAD_QUALITIES).optional().describe('Sub-classification for road paths'),
  name:        z.string().optional(),
  description: z.string().optional(),
  vertices:    z.array(MapPathVertexSchema),
  style:       MapPathStyleSchema,
  // Future: metadata: z.record(z.unknown()).optional()
});
export type MapEntityPath = z.infer<typeof MapEntityPathSchema>;

// ---------------------------------------------------------------------------
// Labels feature
// ---------------------------------------------------------------------------

/** Supported font families for map labels */
export const MAP_LABEL_FONTS = ['Cinzel', 'MedievalSharp', 'Berkshire Swash', 'sans-serif', 'serif'] as const;
export type MapLabelFont = typeof MAP_LABEL_FONTS[number];

/**
 * A free-floating text label on the map.
 */
export const MapLabelSchema = z.object({
  uid: z.string(),
  text: z.string(),
  x: z.number(),
  y: z.number(),
  fontSize: z.number().min(8).max(120).default(24),
  color: z.string().default('#ffffff'),
  fontFamily: z.enum(MAP_LABEL_FONTS).default('Cinzel'),
  fontWeight: z.enum(['normal', 'bold']).default('bold'),
  fontStyle: z.enum(['normal', 'italic']).default('normal'),
  outlineColor: z.string().default('#000000'),
  outlineWidth: z.number().min(0).max(10).default(3),
  rotation: z.number().default(0), // in radians
});
export type MapLabel = z.infer<typeof MapLabelSchema>;

// ---------------------------------------------------------------------------
// Location
// ---------------------------------------------------------------------------

/**
 * Location schema.
 * Master source for Location entity structure.
 */
export const LocationSchema = EntitySchema.extend({
  parentId: z.string().nullable().optional().describe('The ID of the parent location in the hierarchy.'),
  parentName: z.string().nullable().optional().describe('The name of the parent location, for quick display.'),
  type: z.string().optional().describe('Type of Location (city, forest, etc.)'),
  region: z.string().optional().describe('Region / Territory'),

  // Map specific fields
  mapImageUrl: z.string().url().optional().nullable(),
  mapImageStoragePath: z.string().optional().nullable(),
  originalMapUrl: z.string().url().optional().nullable(),
  originalMapStoragePath: z.string().optional().nullable(),
  mapLandUrl: z.string().url().optional().nullable(),
  mapLandStoragePath: z.string().optional().nullable(),
  mapColorUrl: z.string().url().optional().nullable(),
  mapColorStoragePath: z.string().optional().nullable(),
  mapSymbolsUrl: z.string().url().optional().nullable(),
  mapSymbolsStoragePath: z.string().optional().nullable(),
  mapData: z.string().optional().nullable().describe('Compressed hex string'),
  placedSymbols: z.array(z.object({
    uid: z.string(),
    id: z.string(),
    x: z.number(),
    y: z.number(),
    size: z.number(),
    rotation: z.number().optional(),
  })).optional().nullable().describe('Structured list of symbols placed on the map'),
  mapEntityPoints: z.array(MapEntityPointSchema).optional().nullable().describe('World entity pins placed on the map'),
  mapEntityAreas: z.array(MapEntityAreaSchema).optional().nullable().describe('World entity polygon areas drawn on the map'),
  mapEntityPaths: z.array(MapEntityPathSchema).optional().nullable().describe('Polyline paths (roads, rivers, etc.) drawn on the map'),
  mapLabels: z.array(MapLabelSchema).optional().nullable().describe('Free-floating text labels placed on the map'),
  mapWidth: z.number().int().optional().describe('Map buffer width in pixels (used for coordinate normalization)'),
  mapHeight: z.number().int().optional().describe('Map buffer height in pixels (used for coordinate normalization)'),
});

export type Location = z.infer<typeof LocationSchema>;
